/**
 * Connection Manager
 * Gère les connexions WebSocket Realtime
 * Évite les connexions multiples pour la même table
 */

import { supabase } from '@/integrations/supabase/client'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { healthMonitor } from './healthCheck'
import { logger } from '@/utils/productionLogger'
import { LIMITS } from '@/config/features'

interface ChannelInfo {
  channel: RealtimeChannel
  subscribers: number
  createdAt: number
}

export class ConnectionManager {
  private channels = new Map<string, ChannelInfo>()
  
  /**
   * Obtient ou crée une connexion pour une table
   * Réutilise les connexions existantes (singleton pattern)
   */
  getOrCreateChannel(table: string, filter?: string): RealtimeChannel | null {
    // ✅ ÉTAPE 4: Normaliser le filter (trim, pas de undefined)
    const normalizedFilter = filter?.trim() || undefined
    const key = this.getChannelKey(table, normalizedFilter)
    
    // ✅ AUDIT 2: Logger l'état avant décision
    logger.info('[ConnectionManager] getOrCreateChannel called', {
      table,
      filter: normalizedFilter,
      channelKey: key,
      currentChannelsSize: this.channels.size,
      channelExists: this.channels.has(key),
      limit: LIMITS.MAX_REALTIME_CONNECTIONS
    })
    
    // Vérifier la limite de connexions
    if (this.channels.size >= LIMITS.MAX_REALTIME_CONNECTIONS && !this.channels.has(key)) {
      logger.error('[ConnectionManager] Max connections reached', undefined, {
        currentSize: this.channels.size,
        limit: LIMITS.MAX_REALTIME_CONNECTIONS,
        table,
        attemptedKey: key
      })
      return null
    }
    
    // Réutiliser la connexion existante
    if (this.channels.has(key)) {
      const info = this.channels.get(key)
      if (!info) return null // Type guard
      
      const subscribersBefore = info.subscribers
      info.subscribers++
      
      // ✅ ÉTAPE 4: Log REUSE_CHANNEL
      logger.info('[ConnectionManager] REUSE_CHANNEL', {
        table,
        channelKey: key,
        subscribersBefore,
        subscribersAfter: info.subscribers,
        channelTopic: info.channel.topic,
        channelAge: Date.now() - info.createdAt
      })
      return info.channel
    }
    
    // ✅ ÉTAPE 4: Créer une nouvelle connexion avec nom stable et normalisé
    // Note: Supabase ajoute automatiquement le préfixe "realtime:" au topic
    const channelName = `${table}${normalizedFilter ? `:${normalizedFilter}` : ''}`
    const channel = supabase.channel(channelName)
    
    this.channels.set(key, {
      channel,
      subscribers: 1,
      createdAt: Date.now(),
    })
    
    healthMonitor.incrementConnections()
    
    // ✅ ÉTAPE 4: Log CREATE_CHANNEL
    logger.info('[ConnectionManager] CREATE_CHANNEL', {
      table,
      channelKey: key,
      channelName,
      channelTopic: channel.topic,
      totalChannels: this.channels.size,
      subscribers: 1
    })
    
    return channel
  }
  
  /**
   * Libère une connexion
   * Ne ferme la connexion que si plus aucun subscriber
   * ✅ ÉTAPE 3: Idempotent et logs détaillés
   * ✅ CORRECTION 2.1: Normaliser filter pour cohérence avec getOrCreateChannel
   */
  releaseChannel(table: string, filter?: string) {
    // ✅ CORRECTION 2.1: Normaliser le filter (même logique que getOrCreateChannel)
    const normalizedFilter = filter?.trim() || undefined
    const key = this.getChannelKey(table, normalizedFilter)
    const info = this.channels.get(key)
    
    // ✅ AUDIT 3: Logger avant release
    logger.info('[ConnectionManager] releaseChannel called', {
      table,
      filter,
      channelKey: key,
      channelExists: !!info,
      subscribersBefore: info?.subscribers,
      channelTopic: info?.channel.topic,
      totalChannels: this.channels.size
    })
    
    // ✅ ÉTAPE 3: Idempotent - si channel n'existe pas, no-op
    if (!info) {
      logger.warn('[ConnectionManager] releaseChannel no-op (channel not found)', { 
        table,
        filter,
        channelKey: key
      })
      return
    }
    
    // ✅ ÉTAPE 3: Décrémentation sécurisée
    const subscribersBefore = info.subscribers
    info.subscribers = Math.max(0, info.subscribers - 1)
    const subscribersAfter = info.subscribers
    
    logger.info('[ConnectionManager] DECR_SUBSCRIBERS', {
      table,
      channelKey: key,
      subscribersBefore,
      subscribersAfter,
      channelTopic: info.channel.topic
    })
    
    // Si plus de subscribers, fermer la connexion
    if (info.subscribers <= 0) {
      supabase.removeChannel(info.channel)
      this.channels.delete(key)
      healthMonitor.decrementConnections()
      
      logger.info('[ConnectionManager] REMOVE_CHANNEL', {
        table,
        channelKey: key,
        channelTopic: info.channel.topic,
        remainingChannels: this.channels.size
      })
    } else {
      logger.info('[ConnectionManager] Channel kept alive', {
        table,
        channelKey: key,
        remainingSubscribers: info.subscribers,
        channelTopic: info.channel.topic
      })
    }
  }
  
  /**
   * Ferme toutes les connexions
   */
  closeAll() {
    logger.info('[ConnectionManager] Closing all channels', {
      count: this.channels.size
    })
    
    this.channels.forEach((info) => {
      supabase.removeChannel(info.channel)
      healthMonitor.decrementConnections()
    })
    
    this.channels.clear()
  }
  
  /**
   * Retourne les statistiques
   */
  getStats() {
    const stats = {
      totalChannels: this.channels.size,
      channels: Array.from(this.channels.entries()).map(([key, info]) => ({
        key,
        subscribers: info.subscribers,
        age: Date.now() - info.createdAt,
      }))
    }
    
    return stats
  }
  
  /**
   * Génère une clé unique pour un channel
   */
  private getChannelKey(table: string, filter?: string): string {
    return filter ? `${table}:${filter}` : table
  }
}

// Instance globale du connection manager
export const connectionManager = new ConnectionManager()

// Cleanup au déchargement de la page
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    connectionManager.closeAll()
  })
}
